/**
 * Trips API Service
 * Handles saved trips CRUD operations
 */

export class TripsService {
  constructor(apiClient, errorHandler) {
    this.apiClient = apiClient;
    this.handleApiError = errorHandler;
  }

  /**
   * Get user's saved trips
   * @returns {Promise<Array>} Array of saved trips
   */
  async getSavedTrips() {
    try {
      const response = await this.apiClient.get('/trips/');  // Added trailing slash to avoid redirect
      return response.data;
    } catch (error) {
      console.error('Error getting saved trips:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Delete a saved trip
   * @param {string} tripId - ID of the trip to delete
   * @returns {Promise<Object>} Success response
   */
  async deleteTrip(tripId) {
    try {
      const response = await this.apiClient.delete(`/trips/${tripId}`);  // Remove trailing slash to avoid redirect
      return response.data;
    } catch (error) {
      console.error('Error deleting trip:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Get a specific trip by ID
   * @param {string} tripId - ID of the trip to retrieve
   * @returns {Promise<Object>} Trip data
   */
  async getTrip(tripId) {
    try {
      const response = await this.apiClient.get(`/trips/${tripId}`); // Removed trailing slash to avoid redirect network error
      return response.data;
    } catch (error) {
      console.error('Error getting trip:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Update a saved trip
   * @param {string} tripId - ID of the trip to update
   * @param {Object} tripData - Updated trip data
   * @returns {Promise<Object>} Updated trip data
   */
  async updateTrip(tripId, tripData) {
    try {
      const response = await this.apiClient.put(`/trips/${tripId}`, tripData); // Removed trailing slash to avoid redirect network error
      return response.data;
    } catch (error) {
      console.error('Error updating trip:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Create a new trip
   * @param {Object} tripData - Trip data
   * @param {string} tripData.name - Trip name
   * @param {Object} tripData.search_params - Original search parameters
   * @param {Object} tripData.results - Trip results
   * @param {Object} tripData.enriched_results - Enriched results (optional)
   * @param {string} tripData.notes - Notes (optional)
   * @param {boolean} tripData.is_public - Whether trip is public
   * @returns {Promise<Object>} Created trip data
   */
  async createTrip(tripData) {
    try {
      const response = await this.apiClient.post('/trips/', tripData);
      return response.data;
    } catch (error) {
      console.error('Error creating trip:', error);
      throw this.handleApiError(error);
    }
  }
}

export default TripsService;